import React, { useState, useCallback } from 'react';
import { View, ScrollView, StyleSheet, Text, TouchableOpacity, Platform, SafeAreaView, Switch } from 'react-native';
import { isPhone, navigateTo } from '@helpers';
import { scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import StressTestControls from './StressTestControls';
import StressTestResults from './StressTestResults';
import useStressTest from './useStressTest';
import usePerformanceMetrics from './usePerformanceMetrics';

export interface StressTestConfig {
  concurrentRequests: 5 | 10 | 20 | 50 | 100;
  workarounds: {
    useSetTimeout: boolean;
    timeoutDelay: number;
    useInteractionManager: boolean;
    useBatching: boolean;
    batchSize: number;
  };
  metrics: {
    trackNetworkTiming: boolean;
    trackReduxPerformance: boolean;
    trackMemory: boolean;
    trackRendering: boolean;
    trackJSThread: boolean;
  };
}

const StressTestPage = ({ C }) => {
  const colors = {
    background: C('Background / Light / Detail [mobile]'),
    text: C('Label / Light / Primary'),
    textSecondary: C('Label / Light / Secondary'),
    border: C('Hairline/Light/No trans'),
    card: C('Background/Light/Field [mobile]'),
    primary: C('Selected/Light/Primary'),
    secondary: C('Label / Light / Secondary'),
    buttonText: '#ffffff',
    success: '#28a745',
    error: '#dc3545',
    warning: '#ffc107',
  };

  const [config, setConfig] = useState<StressTestConfig>({
    concurrentRequests: 10,
    workarounds: {
      useSetTimeout: false,
      timeoutDelay: 100,
      useInteractionManager: false,
      useBatching: false,
      batchSize: 5,
    },
    metrics: {
      trackNetworkTiming: true,
      trackReduxPerformance: true,
      trackMemory: true,
      trackRendering: true,
      trackJSThread: true,
    },
  });

  const [showResults, setShowResults] = useState(false);

  const { isRunning, networkRequests, reduxOperations, startTest, stopTest, clearResults, exportResults } = useStressTest(config);

  const { metrics, resetMetrics } = usePerformanceMetrics(config.metrics);

  const handleStartTest = useCallback(() => {
    resetMetrics();
    startTest();
    setShowResults(true);
  }, [startTest, resetMetrics]);

  const handleStopTest = useCallback(() => {
    stopTest();
  }, [stopTest]);

  const handleClearResults = useCallback(() => {
    clearResults();
    resetMetrics();
    setShowResults(false);
  }, [clearResults, resetMetrics]);

  const handleExportResults = useCallback(async () => {
    try {
      await exportResults(metrics);
      // Show success feedback
    } catch (error) {
      // Show error feedback
    }
  }, [exportResults, metrics]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: scale(16),
      backgroundColor: colors.card,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      paddingTop: isPhone ? scale(40) : scale(16),
    },
    headerTitle: {
      fontSize: scale(18),
      fontWeight: '700',
      color: colors.text,
      flex: 1,
      textAlign: 'center',
    },
    backButton: {
      padding: scale(8),
      position: 'absolute',
      left: scale(8),
      top: isPhone ? scale(36) : scale(12),
      zIndex: 1,
    },
    backButtonText: {
      fontSize: scale(16),
      color: colors.primary,
      fontWeight: '600',
    },
    content: {
      flex: 1,
    },
    controlsSection: {
      backgroundColor: colors.card,
      margin: scale(16),
      borderRadius: scale(12),
      padding: scale(16),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    metricsBar: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-around',
      padding: scale(12),
      backgroundColor: colors.card,
      margin: scale(16),
      marginTop: 0,
      borderRadius: scale(12),
    },
    metricItem: {
      alignItems: 'center',
      minWidth: scale(80),
      marginVertical: scale(8),
    },
    metricLabel: {
      fontSize: scale(11),
      color: colors.textSecondary,
      marginBottom: scale(4),
    },
    metricValue: {
      fontSize: scale(16),
      fontWeight: '700',
      color: colors.text,
    },
    resultsContainer: {
      flex: 1,
      margin: scale(16),
      marginTop: 0,
    },
    tabBar: {
      flexDirection: 'row',
      backgroundColor: colors.card,
      borderRadius: scale(12),
      marginBottom: scale(12),
      padding: scale(4),
    },
    tab: {
      flex: 1,
      paddingVertical: scale(10),
      alignItems: 'center',
      borderRadius: scale(8),
    },
    activeTab: {
      backgroundColor: colors.primary,
    },
    tabText: {
      fontSize: scale(14),
      fontWeight: '600',
      color: colors.textSecondary,
    },
    activeTabText: {
      color: colors.buttonText,
    },
    resultsContent: {
      backgroundColor: colors.card,
      borderRadius: scale(12),
      padding: scale(12),
      flex: 1,
    },
    actionButtons: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      padding: scale(16),
      backgroundColor: colors.card,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    button: {
      paddingVertical: scale(12),
      paddingHorizontal: scale(24),
      borderRadius: scale(8),
      minWidth: scale(100),
      alignItems: 'center',
    },
    primaryButton: {
      backgroundColor: colors.primary,
    },
    secondaryButton: {
      backgroundColor: colors.card,
      borderWidth: 1,
      borderColor: colors.primary,
    },
    dangerButton: {
      backgroundColor: colors.error,
    },
    buttonText: {
      fontSize: scale(14),
      fontWeight: '600',
      color: C('Button/Light/Primary Text'),
    },
    secondaryButtonText: {
      fontSize: scale(14),
      fontWeight: '600',
      color: C('Selected/Light/Primary'),
    },
  });

  const [activeTab, setActiveTab] = useState<'network' | 'redux'>('network');

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('DevInfo')}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Network Stress Test</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Controls */}
        <View style={styles.controlsSection}>
          <StressTestControls config={config} onConfigChange={setConfig} isRunning={isRunning} colors={colors} isPhone={isPhone} />
        </View>

        {/* Metrics Bar */}
        {showResults && (
          <View style={styles.metricsBar}>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>Avg Response</Text>
              <Text style={styles.metricValue}>{metrics.avgResponseTime ? `${metrics.avgResponseTime.toFixed(0)}ms` : '-'}</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>JS Blocked</Text>
              <Text style={styles.metricValue}>{metrics.jsThreadBlockTime ? `${metrics.jsThreadBlockTime.toFixed(0)}ms` : '-'}</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>Memory</Text>
              <Text style={styles.metricValue}>{metrics.memoryUsed ? `${(metrics.memoryUsed / 1024 / 1024).toFixed(1)}MB` : '-'}</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>Redux Ops</Text>
              <Text style={styles.metricValue}>{metrics.reduxDispatchCount || '-'}</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>Success Rate</Text>
              <Text style={styles.metricValue}>
                {networkRequests.length > 0
                  ? `${Math.round((networkRequests.filter((r) => r.status === 'success').length / networkRequests.length) * 100)}%`
                  : '-'}
              </Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>Total Requests</Text>
              <Text style={styles.metricValue}>{networkRequests.length || '-'}</Text>
            </View>
          </View>
        )}

        {/* Results */}
        {showResults && (
          <View style={styles.resultsContainer}>
            {/* Tab Bar */}
            <View style={styles.tabBar}>
              <TouchableOpacity style={[styles.tab, activeTab === 'network' && styles.activeTab]} onPress={() => setActiveTab('network')}>
                <Text style={[styles.tabText, activeTab === 'network' && styles.activeTabText]}>Network ({networkRequests.length})</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.tab, activeTab === 'redux' && styles.activeTab]} onPress={() => setActiveTab('redux')}>
                <Text style={[styles.tabText, activeTab === 'redux' && styles.activeTabText]}>Redux ({reduxOperations.length})</Text>
              </TouchableOpacity>
            </View>

            {/* Results Content */}
            <View style={styles.resultsContent}>
              <StressTestResults
                type={activeTab}
                data={activeTab === 'network' ? networkRequests : reduxOperations}
                isRunning={isRunning}
                colors={colors}
                isPhone={isPhone}
              />
            </View>
          </View>
        )}
      </ScrollView>

      {/* Action Buttons */}
      <View style={styles.actionButtons}>
        <TouchableOpacity
          style={[styles.button, isRunning ? styles.dangerButton : styles.primaryButton]}
          onPress={isRunning ? handleStopTest : handleStartTest}
        >
          <Text style={styles.buttonText}>{isRunning ? 'Stop' : 'Start Test'}</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.button, styles.secondaryButton]} onPress={handleClearResults} disabled={isRunning}>
          <Text style={styles.secondaryButtonText}>Clear</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.button, styles.secondaryButton]}
          onPress={handleExportResults}
          disabled={isRunning || networkRequests.length === 0}
        >
          <Text style={styles.secondaryButtonText}>Export</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

export default ColorWrapper(StressTestPage);
